/**
 * Textos de la propuesta por enlace. Son los del dossier «a vuestra medida»
 * (`marketing/dossiers/content-lola.mjs`) SIN PRECIOS: el dossier con precios
 * solo se manda cuando hay llamada fijada.
 */
import { MONTAJES_VISUALES } from "@/features/presentacion/montajes";

/** El mismo vídeo para todas las parejas; la web superpone su nombre. */
export const VIDEO_PROPUESTA = "/propuesta/video.mp4";
export const POSTER_PROPUESTA = "/propuesta/poster.jpg";

export interface Bloque {
  n: string;
  titulo: string;
  texto: string;
}

export interface SeccionMomentos {
  kicker: string;
  titulo: readonly [string, string];
  entrada: string;
  bloques: readonly Bloque[];
  notas: readonly (readonly [string, string])[];
}

export const SONIDO: SeccionMomentos = {
  kicker: "El sonido",
  titulo: ["NOS ENCARGAMOS", "DE TODO EL SONIDO."],
  entrada:
    "De la ceremonia a la última canción. Nos adaptamos a cómo funciona vuestra boda, no al revés.",
  bloques: [
    {
      n: "01",
      titulo: "Ceremonia y cóctel",
      texto:
        "Sonido para la ceremonia y ambiente desde que llegan los invitados, con el estilo que nos digáis.",
    },
    {
      n: "02",
      titulo: "Comida o cena",
      texto:
        "Volumen para poder hablar y las canciones que elijáis para entrar y para los momentos especiales.",
    },
    {
      n: "03",
      titulo: "Fiesta",
      texto:
        "Desde la música de los 80 y los 90 hasta lo último, leyendo la pista: que bailen desde los abuelos hasta los más jóvenes.",
    },
  ],
  notas: [
    [
      "Grupos y música en directo",
      "Si lleváis grupo, coro, flamenco o cualquier música en directo, lo sonorizamos y nos coordinamos con ellos para que todo suene bien y sin cortes.",
    ],
    [
      "Preparación previa",
      "Antes de la boda repasamos con vosotros cómo queréis cada momento, las canciones que no pueden faltar y las que no queréis oír.",
    ],
  ],
};

export const PRODUCCION: SeccionMomentos = {
  kicker: "La producción",
  titulo: ["EN DIRECTO", "O AL SEGUNDO."],
  entrada:
    "El DJ no solo pone la música: maneja a la vez el sonido, las luces y los efectos, en directo durante la fiesta y programados al segundo en los momentos clave.",
  bloques: [
    {
      n: "01",
      titulo: "Sonido",
      texto:
        "Equipo propio adaptado al espacio, con subgraves y altavoces de rango completo para que se oiga bien en toda la pista.",
    },
    {
      n: "02",
      titulo: "Luces",
      texto:
        "En la fiesta se manejan en directo; en el baile o la entrada, se programan para que cada cosa pase en el instante justo.",
    },
    {
      n: "03",
      titulo: "Efectos",
      texto:
        "Humo, CO₂, fuego frío y confeti se disparan desde la cabina justo cuando la pista lo pide.",
    },
  ],
  notas: [
    [
      "Una sola persona al mando",
      "Música, luces y efectos van coordinados porque los controla quien está leyendo la pista en ese momento.",
    ],
    [
      "Montaje propio",
      "Montamos, configuramos y desmontamos todo nosotros: no tenéis que coordinar a ningún técnico.",
    ],
  ],
};

export interface Pack {
  etiqueta: string;
  nombre: string;
  entrada: string;
  incluye: readonly string[];
  imagen: string;
  alt: string;
}

const visual = (id: string) => {
  const montaje = MONTAJES_VISUALES.find((m) => m.id === id);
  if (!montaje) throw new Error(`Montaje desconocido: ${id}`);
  return montaje;
};

export const PACKS: readonly Pack[] = [
  {
    etiqueta: "Pack 01 · Punto de partida",
    nombre: "EXPERIENCE",
    entrada:
      "Producción completa para acompañar toda la boda con sonido, iluminación y presencia visual.",
    incluye: [
      "DJ y música durante el cóctel, la comida o cena y la fiesta.",
      "Preparación musical previa con la pareja.",
      "Cabina de madera SUSIEDAD.",
      "Sistema de sonido: 2 subgraves y 4 altavoces de rango completo.",
      "Puente de iluminación con 4 focos beam y 4 flashes estroboscópicos.",
      "2 máquinas de humo vertical.",
      "Montaje, configuración y desmontaje.",
    ],
    imagen: visual("experience").imagen,
    alt: visual("experience").alt,
  },
  {
    etiqueta: "Pack 02 · Punto de partida",
    nombre: "SIGNATURE",
    entrada: "Más movimiento, más profundidad y una puesta en escena con presencia de show.",
    incluye: [
      "Todo lo incluido en el pack Experience.",
      "4 cabezas móviles wash para ampliar el diseño de iluminación.",
      "2 de ellas sobre torres de madera integradas en el montaje.",
      "Máquina de humo extra para dar un mejor efecto a toda la producción.",
      "Modificación personalizada de canciones para entradas, bailes y momentos especiales.",
      "Mayor dinamismo visual y cobertura de pista.",
    ],
    imagen: visual("signature").imagen,
    alt: visual("signature").alt,
  },
];

export interface Estructura {
  nombre: string;
  texto: string;
  imagen: string;
  alt: string;
}

const TEXTO_ESTRUCTURA: Record<string, string> = {
  columnas: "Columnas de madera a los lados y nada por encima de la pista.",
  monolito: "Dos paneles de pantalla detrás de la cabina.",
  portico: "Una estructura cuadrada que enmarca la cabina.",
  triangulo: "Cabezas móviles repartidas por las diagonales.",
  arco: "Un aro de luz casi cerrado alrededor de la cabina.",
};

export const A_MEDIDA = {
  kicker: "A vuestra medida",
  titulo: ["Y SI QUERÉIS MÁS,", "LO DISEÑAMOS."] as const,
  entrada:
    "Los packs son el punto de partida. A partir de ahí diseñamos el montaje completo tal y como lo imaginéis: estructura, luz, pantallas y efectos, pensados para vuestro sitio.",
  estructuras: MONTAJES_VISUALES.filter((m) => m.tipo === "medida").map((m): Estructura => ({
    nombre: m.nombre,
    texto: TEXTO_ESTRUCTURA[m.id] ?? "",
    imagen: m.imagen,
    alt: m.alt,
  })),
  cierre: ["Y LO QUE IMAGINÉIS.", "Os lo diseñamos a vuestro gusto."] as const,
  aviso: "Simulaciones de montaje. La estructura final se dimensiona sobre el recinto real.",
};

export const EXTRAS = {
  kicker: "Para hacerla única",
  titulo: "EXTRAS",
  entrada:
    "Las luces y los efectos los maneja el propio DJ desde la cabina: en directo cuando la pista lo pide, o programados al segundo para vuestro baile.",
  sub: "Los efectos y otros extras se pueden añadir a cualquier pack o diseño a medida. Os contamos cuáles encajan en vuestro sitio.",
  lista: [
    "Fuego frío",
    "Jets de CO₂",
    "Pistola de CO₂",
    "Máquina de confeti",
    "Pistola de confeti",
    "Pantallas LED",
    "Photocall",
    "Cámara 360",
    "Tarima para la cabina",
    "Decoración de la zona DJ a vuestro gusto",
    "Iluminación ambiental",
    "Hora loca",
  ],
  aviso:
    "Los efectos están sujetos a las condiciones del recinto, a los permisos y a las distancias de seguridad. Cada propuesta se revisa técnicamente antes de confirmarla.",
};

export const SIGUIENTE_PASO = {
  kicker: "Siguiente paso",
  claim: ["VUESTRA BODA.", "VUESTRA NOCHE."] as const,
  entrada: "Diseñamos juntos vuestra boda, cerramos los extras y reservamos vuestra fecha.",
  pasos: [
    ["01", "Diseñamos juntos vuestra boda"],
    ["02", "Canciones y momentos clave"],
    ["03", "Pack o diseño a medida y extras"],
    ["04", "Reserva de la fecha"],
  ] as const,
};

/** WhatsApp de Juan, con el mensaje ya escrito. */
export function enlaceWhatsApp(nombre: string): string {
  const texto = `¡Hola Juan! Soy ${nombre}. He visto la propuesta y me gustaría hablar de nuestra boda.`;
  return `https://wa.me/34633199708?text=${encodeURIComponent(texto)}`;
}

/** "6 de marzo de 2027", o null si no hay fecha. */
export function fechaLarga(fecha: string | null): string | null {
  if (!fecha) return null;
  const [anio, mes, dia] = fecha.split("-").map(Number);
  return new Intl.DateTimeFormat("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(anio, mes - 1, dia)));
}

/**
 * Nombre de la portada y de la vista previa (igual que `crm_nombre_portada`):
 * una pareja («Marta y Rubén») va entera; nombre y apellidos, solo el nombre.
 */
export function nombrePortada(nombre: string): string {
  const limpio = nombre.trim().replace(/\s+/g, " ");
  return /\s(y|e|&)\s/i.test(limpio) ? limpio : (limpio.split(" ")[0] ?? "");
}
